import React, { useState } from 'react'; // v1.1
import { Button, NavBar } from 'antd-mobile';
import { motion, AnimatePresence } from 'framer-motion';
import { usePuffApi } from '../hooks/usePuffApi';
import { useStore } from '../store/useStore';
import { theme } from '../theme';


const NumInput = ({ value, onChange, presets, suffix, placeholder }: {
  value: number; onChange: (v: number) => void; presets: number[]; suffix?: string; placeholder?: string;
}) => (
  <div style={{ width: '100%' }}>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 12 }}>
      {presets.map(v => (
        <button key={v} onClick={() => onChange(v)} style={{
          flex: 1, padding: '10px 0', borderRadius: 12, fontSize: 14, fontWeight: 700,
          border: value === v ? '2px solid #111827' : '1px solid #e5e7eb',
          background: value === v ? '#111827' : 'white',
          color: value === v ? 'white' : '#4b5563',
          cursor: 'pointer', transition: 'all 0.2s ease', whiteSpace: 'nowrap'
        }}>
          {suffix === '฿' ? `฿${v.toLocaleString()}` : v.toLocaleString()}{suffix && suffix !== '฿' ? ` ${suffix}` : ''}
        </button>
      ))}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
      <input
        type="text" inputMode="numeric" pattern="[0-9]*"
        value={value === 0 ? '' : String(value)}
        onChange={e => {
          const raw = e.target.value.replace(/[^0-9]/g, '');
          onChange(raw === '' ? 0 : parseInt(raw, 10));
        }}
        placeholder={placeholder || '0'}
        style={{
          width: '100%', maxWidth: 200, padding: '10px', borderRadius: 12,
          border: '1px solid #e5e7eb', fontSize: 20, fontWeight: 800,
          textAlign: 'center', outline: 'none', color: '#111827',
          background: 'white', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.02)'
        }}
      />
    </div>
    {suffix && <div style={{ textAlign: 'center', marginTop: 4, color: '#9ca3af', fontSize: 12 }}>หน่วย: {suffix}</div>}
  </div>
);

export const Onboarding: React.FC = () => {
  const { completeOnboarding } = usePuffApi();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const primaryColor = theme.primary;

  // Form state
  const [smokingType, setSmokingType] = useState<'pod' | 'cigarette' | ''>('');
  const [smokingDuration, setSmokingDuration] = useState('');

  // Cigarette fields with DEFAULTS
  const [dailyCigarettes, setDailyCigarettes] = useState(12);
  const [packPrice, setPackPrice] = useState(100);

  // Pod fields with DEFAULTS
  const [podPrice, setPodPrice] = useState(250);
  const [puffsPerPod, setPuffsPerPod] = useState(5000);
  const [dailyPuffs, setDailyPuffs] = useState(300);

  // Shared
  const [quitReasons] = useState<string[]>([]);
  const [mode, setMode] = useState<'quit' | 'reduce' | null>(null);
  const [dailyLimit, setDailyLimit] = useState(15);

  const totalSteps = 4;

  // ── Calculations ──
  const dailyAmount = smokingType === 'cigarette' ? dailyCigarettes : dailyPuffs;
  const unit = smokingType === 'cigarette' ? 'มวน' : 'คำ';

  const costPerPuff = smokingType === 'cigarette'
    ? (packPrice > 0 ? packPrice / 20 : 0)
    : (podPrice > 0 && puffsPerPod > 0 ? podPrice / puffsPerPod : 0);

  const monthlyCost = Math.round(dailyAmount * costPerPuff * 30);

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setDirection(1);
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setDirection(-1);
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    if (!mode || !smokingType) return;
    setIsLoading(true);

    const settings = {
      mode,
      dailyPuffLimit: mode === 'quit' ? 0 : dailyLimit,
      costPerPuff: Math.round(costPerPuff * 100) / 100,
      nicotinePerPuff: smokingType === 'pod' ? 0.05 : 0.1,
      smokingType,
      smokingDuration,
      dailyAmount,
      quitReasons,
      monthlyCost,
      ...(mode === 'quit' ? { quitDate: new Date() } : {}),
      ...(smokingType === 'pod' ? { puffsPerPod, podPrice } : {}),
    };

    try {
      await completeOnboarding(settings);
    } catch (e) {
      console.error('Onboarding API failed, proceeding anyway:', e);
      // Even if API fails, save locally so user isn't stuck
      const { setUser, setOnboardingCompleted, user: currentUser } = useStore.getState();
      if (currentUser) {
        setUser({ ...currentUser, ...settings, onboardingCompleted: true } as any);
      }
      setOnboardingCompleted(true);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Compact Option Card ──
  // Use 'compact' prop to reduce size for crowded pages
  const OptionCard = ({ icon, label, sublabel, selected, onClick, compact }: {
    icon: React.ReactNode; label: string; sublabel?: string; selected: boolean; onClick: () => void; compact?: boolean;
  }) => (
    <motion.button
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: compact ? 16 : 24,
        width: '100%', padding: compact ? '16px' : '24px', borderRadius: 24,
        border: selected ? `2px solid #111827` : '1px solid #e5e7eb',
        background: 'white',
        cursor: 'pointer', textAlign: 'left',
        marginBottom: compact ? 10 : 16,
        position: 'relative',
        boxShadow: selected ? `0 8px 20px -6px rgba(0,0,0,0.1)` : '0 2px 4px -1px rgba(0, 0, 0, 0.02)',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{
        width: compact ? 56 : 72, height: compact ? 56 : 72, borderRadius: 16,
        background: selected ? '#f3f4f6' : '#f9fafb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: compact ? 28 : 36, flexShrink: 0,
        transition: 'all 0.3s ease',
        overflow: 'hidden'
      }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: compact ? 16 : 18, color: '#111827', marginBottom: compact ? 2 : 6 }}>{label}</div>
        {sublabel && <div style={{ fontSize: compact ? 12 : 14, color: '#6b7280', lineHeight: 1.4 }}>{sublabel}</div>}
      </div>

      <div style={{
        width: compact ? 22 : 28, height: compact ? 22 : 28, borderRadius: '50%',
        border: selected ? '6px solid #111827' : '2px solid #e5e7eb',
        background: 'white', flexShrink: 0, transition: 'all 0.3s ease'
      }} />
    </motion.button>
  );

  const SquareCard = ({ icon, label, sublabel, selected, onClick }: {
    icon: React.ReactNode; label: string; sublabel?: string; selected: boolean; onClick: () => void;
  }) => (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
        padding: '24px 12px 16px', borderRadius: 24,
        border: selected ? `2px solid ${theme.primary}` : '1px solid #e5e7eb',
        background: selected ? `${theme.primary}08` : 'white',
        cursor: 'pointer', textAlign: 'center',
        position: 'relative',
        boxShadow: selected ? `0 8px 24px -6px rgba(122, 32, 32, 0.15)` : '0 2px 4px -1px rgba(0, 0, 0, 0.02)',
        transition: 'all 0.2s ease',
        width: '100%',
        aspectRatio: '0.78',
      }}
    >
      <div style={{
        width: '100%', flex: 1, minHeight: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 12,
      }}>
        {icon}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: 45 }}>
        <div style={{ fontWeight: 800, fontSize: 16, color: selected ? theme.primary : '#111827', marginBottom: 2 }}>{label}</div>
        {sublabel && <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.3 }}>{sublabel}</div>}
      </div>
    </motion.button>
  );

  const variants = {
    enter: (direction: number) => ({ x: direction > 0 ? 30 : -30, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({ x: direction < 0 ? 30 : -30, opacity: 0 })
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <>
            <div style={{ textAlign: 'center', marginBottom: 32 }}> {/* Reduced MB */}
              <h1 style={{ fontSize: 28, fontWeight: 900, color: '#111827', margin: 0 }}>ยินดีต้อนรับ!</h1>
              <p style={{ fontSize: 16, color: '#6b7280', marginTop: 8 }}>เริ่มกันที่คำถามง่ายๆ ก่อนนะ</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <SquareCard
                icon={<img src="/pord1.png" style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="บุหรี่ไฟฟ้า" />} label="บุหรี่ไฟฟ้า" sublabel="RELX, INFY, KS และอื่นๆ"
                selected={smokingType === 'pod'} onClick={() => setSmokingType(prev => prev === 'pod' ? '' : 'pod')}
              />
              <SquareCard
                icon={<img src="/smoke1.png" style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="บุหรี่มวน" />} label="บุหรี่มวน" sublabel="ซอง, มวนเอง"
                selected={smokingType === 'cigarette'} onClick={() => setSmokingType(prev => prev === 'cigarette' ? '' : 'cigarette')}
              />
            </div>
          </>
        );

      case 1: // Page 2: Duration - COMPACT MODE
        return (
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}> {/* Reduced MB */}
              <h1 style={{ fontSize: 26, fontWeight: 900, color: '#111827', margin: 0 }}>สูบมานานแค่ไหน?</h1>
            </div>

            <div style={{ display: 'grid', gap: 0, marginBottom: 24 }}> {/* Removed gap, handled by MarginBottom */}
              {[
                { val: '<1y', label: 'น้อยกว่า 1 ปี', icon: '⏳' },
                { val: '1-3y', label: '1 - 3 ปี', icon: '🕒' },
                { val: '3-5y', label: '3 - 5 ปี', icon: '🕘' },
                { val: '>5y', label: 'มากกว่า 5 ปี', icon: '🕰️' },
              ].map((item) => (
                <OptionCard
                  key={item.val}
                  icon={item.icon} label={item.label}
                  selected={smokingDuration === item.val}
                  onClick={() => setSmokingDuration(prev => prev === item.val ? '' : item.val)}
                  compact={true} // Enable compact mode
                />
              ))}
            </div>
          </>
        );

      case 2: // Page 3: Costs & Amounts
        return (
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <h1 style={{ fontSize: 26, fontWeight: 900, color: '#111827', margin: 0 }}>ค่าใช้จ่ายล่ะ?</h1>
            </div>

            {smokingType === 'cigarette' && (
              <div style={{ animation: 'fadeIn 0.5s', display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>💰 ราคาต่อซอง?</h3>
                  <NumInput value={packPrice} onChange={setPackPrice} presets={[70, 100, 120]} suffix="฿" />
                </div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>🚬 วันละกี่มวน?</h3>
                  <div style={{ textAlign: 'center', fontSize: 38, fontWeight: 900, color: '#111827', marginBottom: 4, lineHeight: 1 }}>{dailyCigarettes}</div>
                  <input type="range" min={1} max={60} value={dailyCigarettes} onChange={e => setDailyCigarettes(Number(e.target.value))} style={{ width: '100%', accentColor: 'black', height: 6 }} />
                </div>
              </div>
            )}

            {smokingType === 'pod' && (
              <div style={{ animation: 'fadeIn 0.5s', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>💰 ราคาหัวพอต?</h3>
                  <NumInput value={podPrice} onChange={setPodPrice} presets={[150, 250, 350]} suffix="฿" />
                </div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>⚡ 1 หัวได้กี่คำ?</h3>
                  <NumInput value={puffsPerPod} onChange={setPuffsPerPod} presets={[2000, 5000, 8000]} suffix="คำ" />
                </div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>💨 วันละกี่คำ?</h3>
                  <div style={{ textAlign: 'center', fontSize: 38, fontWeight: 900, color: 'black', marginBottom: 4, lineHeight: 1 }}>{dailyPuffs}</div>
                  <input type="range" min={100} max={1000} step={50} value={dailyPuffs} onChange={e => setDailyPuffs(Number(e.target.value))} style={{ width: '100%', accentColor: 'black', height: 6 }} />
                </div>
              </div>
            )}
          </>
        );

      case 3: // Page 4: Goals
        return (
          <>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <h1 style={{ fontSize: 28, fontWeight: 900, color: '#111827', margin: 0 }}>เป้าหมายปีนี้?</h1>
            </div>

            <OptionCard
              icon="🚫" label="เลิกทันที (Cold Turkey)" sublabel="หยุดสูบตั้งแต่วันนี้ หักดิบเลย"
              selected={mode === 'quit'} onClick={() => setMode(prev => prev === 'quit' ? null : 'quit')}
            />
            <OptionCard
              icon="📉" label="ค่อยๆ ลด (Reduce)" sublabel="ลดปริมาณลงทีละนิด จนกว่าจะเลิกได้"
              selected={mode === 'reduce'} onClick={() => {
                if (mode === 'reduce') setMode(null);
                else { setMode('reduce'); setDailyLimit(Math.max(Math.round(dailyAmount * 0.7), 1)); }
              }}
            />

            {mode === 'reduce' && (
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12, animation: 'fadeIn 0.5s' }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, textAlign: 'center' }}>🎯 เป้าหมายระยะสั้น (ต่อวัน)</h3>
                <div style={{ textAlign: 'center', fontSize: 42, fontWeight: 900, color: '#111827', lineHeight: 1 }}>{dailyLimit} <span style={{ fontSize: 16, color: '#9ca3af' }}>{unit}</span></div>
                <input type="range" min={1} max={dailyAmount} value={dailyLimit} onChange={e => setDailyLimit(Number(e.target.value))} style={{ width: '100%', accentColor: 'black', height: 6, marginTop: 4 }} />
              </div>
            )}
          </>
        );
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return !!smokingType;
      case 1: return !!smokingDuration;
      case 2: {
        if (smokingType === 'cigarette') return dailyCigarettes > 0 && packPrice > 0;
        if (smokingType === 'pod') return podPrice > 0 && dailyPuffs > 0;
        return false;
      }
      case 3: return mode !== null;
      default: return false;
    }
  };

  return (
    <div style={{
      height: '100dvh',
      background: '#ffffff',
      display: 'flex', flexDirection: 'column',
      maxWidth: 480, margin: '0 auto', width: '100%',
      fontFamily: `"Manrope", "Prompt", sans-serif`,
      overflow: 'hidden', touchAction: 'none',
    }}>
      {/* ── Progress Bar Top ── */}
      <div style={{
        padding: '12px 24px 0 24px',
        display: 'flex', gap: 6
      }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: step >= i ? primaryColor : '#f3f4f6', // Theme color if passed/current
            transition: 'all 0.3s ease'
          }} />
        ))}
      </div>

      <NavBar
        back={null}
        onBack={() => { }}
        // REMOVED "Onboarding" Title
        right={<div style={{ color: '#9ca3af', fontWeight: 600, fontSize: 12 }}>{step + 1}/{totalSteps}</div>}
        style={{ '--height': '48px', background: 'transparent' } as any}
      />

      <div style={{ flex: 1, overflow: 'hidden', padding: '0 24px', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={step}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            style={{ width: '100%' }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      <div style={{
        padding: '24px', paddingBottom: 'max(32px, env(safe-area-inset-bottom))',
        background: 'white',
        display: 'flex', gap: 12, alignItems: 'center'
      }}>
        {step > 0 && (
          <Button
            block
            onClick={handlePrev}
            style={{
              flex: 1, height: 52, borderRadius: 26,
              border: '2px solid #e5e7eb', background: 'white',
              color: '#111827', fontWeight: 800, fontSize: 16
            }}
          >
            ย้อนกลับ
          </Button>
        )}

        {step < totalSteps - 1 ? (
          <Button
            block
            onClick={handleNext}
            disabled={!canProceed()}
            style={{
              flex: 2, height: 52, borderRadius: 26, border: 'none',
              background: canProceed() ? primaryColor : '#f3f4f6',
              color: canProceed() ? '#ffffff' : '#9ca3af',
              fontWeight: 800, fontSize: 16,
              boxShadow: canProceed() ? '0 10px 20px rgba(0,0,0,0.15)' : 'none',
              opacity: canProceed() ? 1 : 1,
              transition: 'all 0.2s',
            }}
          >
            ถัดไป →
          </Button>
        ) : (
          <Button
            block
            loading={isLoading}
            disabled={!canProceed()}
            onClick={handleComplete}
            style={{
              flex: 2, height: 52, borderRadius: 26, border: 'none',
              background: primaryColor,
              color: '#ffffff',
              fontWeight: 800, fontSize: 16,
              boxShadow: '0 10px 20px rgba(0,0,0,0.15)'
            }}
          >
            เริ่มต้น 🚀
          </Button>
        )}
      </div>

    </div>
  );
};
